import React, { useState, useRef, useEffect } from "react";
import { MODS } from "../../constants/modules";
import { TYPES } from "../../constants/wineTypes";
import { CameraModal } from "./components/CameraModal";
import { CSVImportModal } from "./components/CSVImportModal";
import { ChatCTA } from "./components/ChatCTA";
import { ChatInputPanel } from "./components/ChatInputPanel";
import { ChatResultPanel } from "./components/ChatResultPanel";
import { QRModal } from "./components/QRModal";
import {
  addChatHistoryEntry,
  clearChatHistory,
  fetchChatHistory,
  generateChatContent,
  type HistoryEntry,
} from "./chatPage.api";

interface ChatPageProps {
  maison: string;
  lang: string;
}

export function ChatPage({ maison, lang }: ChatPageProps) {
  const [wine, setWine] = useState("");
  const [tab, setTab] = useState("fiche");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<"text" | "pdf" | "camera">("text");
  const [pdfTxt, setPdfTxt] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [imgB64, setImgB64] = useState<string | null>(null);
  const [showCam, setShowCam] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [wineType, setWineType] = useState<string | null>(null);
  const [pour, setPour] = useState(0);
  const [showHist, setShowHist] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const pourRef = useRef<ReturnType<typeof setInterval>>();
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchChatHistory().then(setHistory).catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    if (loading) {
      let v = 0; setPour(0);
      pourRef.current = setInterval(() => {
        v += Math.random() * 2.2 + 0.4;
        if (v >= 90) { clearInterval(pourRef.current); v = 90; }
        setPour(Math.min(v, 90));
      }, 85);
    } else {
      clearInterval(pourRef.current);
      if (pour > 0) { setPour(100); setTimeout(() => setPour(0), 480); }
    }
    return () => clearInterval(pourRef.current);
  }, [loading]);

  const handlePDF = async (file: File) => {
    if (!file || file.type !== "application/pdf") return;
    setPdfName(file.name);
    const r = new FileReader();
    r.onload = (e) => {
      const arr = new Uint8Array(e.target!.result as ArrayBuffer);
      const raw = new TextDecoder("utf-8", { fatal: false }).decode(arr);
      const txt = raw.replace(/[^\x20-\x7E\n\rÀ-ɏ]/g, " ").replace(/\s+/g, " ").trim().slice(0, 4000);
      setPdfTxt(txt || "(PDF chargé)");
      setMode("pdf");
      setWine(file.name.replace(/\.pdf$/i, "").replace(/[_-]/g, " "));
    };
    r.readAsArrayBuffer(file);
  };

  const canGen = mode === "text" ? wine.trim().length > 0 : mode === "pdf" ? !!pdfTxt : !!imgB64;
  const key = `${tab}_${lang}`;
  const result = results[key];
  const mod = MODS.find((m) => m.id === tab);
  const wineTypeDef = TYPES.find((t) => t.id === wineType);
  const wineCtx = wine.trim()
    ? (wineType ? `${wine.trim()} — ${wineTypeDef?.short || wineType}` : wine.trim())
    : (pdfName || "Photo étiquette");

  const generate = async () => {
    if (!canGen || loading) return;
    try {
      setLoading(true);
      const { text, prompt } = await generateChatContent({
        tab, wine: wineCtx, lang, maison, pdfTxt, imgB64,
      });
      setResults((r) => ({ ...r, [key]: text }));
      console.log("[VinAI] Prompt qui serait envoyé :", prompt);

      const entry: HistoryEntry = {
        id: Date.now(),
        wine: wine.trim() || pdfName || "Photo",
        wineType,
        tab: mod?.label || "",
        tabId: tab,
        lang,
        text,
        hasNote: false,
        time: new Date().toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      };
      const nextHistory = await addChatHistoryEntry(entry);
      setHistory(nextHistory);
    } catch (e: any) {
      setResults((r) => ({ ...r, [key]: "Erreur : " + e.message }));
    } finally {
      setLoading(false);
    }
  };

  const updateResult = (newText: string) => setResults((r) => ({ ...r, [key]: newText }));

  const handleClearHistory = async () => {
    await clearChatHistory();
    setHistory([]);
  };

  const exportPDF = () => {
    if (!result) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>VINAI — ${wine}</title><style>body{font-family:'Helvetica Neue',sans-serif;max-width:680px;margin:60px auto;color:#1a1008;line-height:1.8}h1{font-size:11px;letter-spacing:4px;text-transform:uppercase;color:#C4824A;border-bottom:1px solid #C4824A;padding-bottom:8px}h2{font-size:22px;font-weight:300;margin:6px 0 24px}.meta{font-size:10px;letter-spacing:2px;color:#888;margin-bottom:32px}pre{white-space:pre-wrap;font-family:inherit;font-size:15px;line-height:1.8}.foot{margin-top:48px;padding-top:12px;border-top:1px solid #eee;font-size:10px;letter-spacing:2px;color:#bbb;display:flex;justify-content:space-between}</style></head><body><h1>VINAI — ${mod?.label}</h1><h2>${wine}</h2><div class="meta">${maison ? maison + " · " : ""}${lang.toUpperCase()} · ${new Date().toLocaleDateString("fr-FR")}</div><pre>${result}</pre><div class="foot"><span>VINAI v5</span><span>${mod?.label}</span></div></body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <main className={`flex-1 overflow-auto flex justify-center ${canGen || result ? "items-start" : "items-center"}`}>
      {showCam && <CameraModal onCapture={(b) => { setImgB64(b); setMode("camera"); setShowCam(false); }} onClose={() => setShowCam(false)} />}
      {showQR && result && <QRModal wine={wine} content={result} onClose={() => setShowQR(false)} />}
      {showCSV && <CSVImportModal onClose={() => setShowCSV(false)} onImport={() => { }} />}

      {showHist && (
        <div role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && setShowHist(false)}
          className="fixed inset-0 z-[200] bg-black/60 flex justify-end">
          <div className="w-[300px] bg-surface border-l border-border px-4 py-5 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <span className="text-[11px] tracking-[3px] text-accent uppercase">Historique</span>
              <div className="flex gap-2">
                {history.length > 0 && <button onClick={handleClearHistory} className="bg-transparent border border-foreground/[0.08] text-secondary cursor-pointer text-[12px] font-sans rounded-[5px] px-2.5 py-1 min-h-[36px]">Effacer</button>}
                <button onClick={() => setShowHist(false)} className="bg-transparent border-none text-secondary cursor-pointer text-xl w-9 h-9 flex items-center justify-center">×</button>
              </div>
            </div>
            {history.length === 0
              ? <p className="text-[13px] text-secondary italic text-center mt-12">Aucune génération</p>
              : history.map((h) => (
                <button key={h.id} onClick={() => { setWine(h.wine); setTab(h.tabId); setResults((r) => ({ ...r, [`${h.tabId}_${h.lang}`]: h.text })); setShowHist(false); }}
                  className="w-full px-2 py-3 bg-transparent border-none border-b border-foreground/[0.04] text-left cursor-pointer rounded font-sans block">
                  <div className="text-[13px] text-secondary italic overflow-hidden text-ellipsis whitespace-nowrap mb-0.5">{h.wine}</div>
                  <div className="flex gap-1.5 items-center">
                    <span className="text-[11px] text-accent/75">{h.tab}</span>
                    <span className="text-[11px] text-secondary/60">{h.lang?.toUpperCase()}</span>
                    <span className="text-[11px] text-secondary/50 ml-auto">{h.time}</span>
                  </div>
                </button>
              ))
            }
          </div>
        </div>
      )}

      <div className={`max-w-[960px] w-full px-5 pb-12 flex flex-col gap-3 ${canGen || result ? "pt-8" : "pt-4.5 justify-center"}`}>

        {/* CTA */}
        {!result && !canGen && <ChatCTA />}

        {/* Input zone */}
        <ChatInputPanel
          canGen={canGen}
          loading={loading}
          mode={mode}
          pdfName={pdfName}
          pour={pour}
          tab={tab}
          wine={wine}
          wineType={wineType}
          onGenerate={generate}
          onOpenCamera={() => { setShowCam(true); setWineType(null); }}
          onOpenCsv={() => setShowCSV(true)}
          onOpenPdf={() => { fileRef.current?.click(); setWineType(null); }}
          onResetMode={() => { setMode("text"); setImgB64(null); setPdfTxt(""); setPdfName(""); }}
          onTabChange={setTab}
          onWineChange={setWine}
          onWineTypeChange={setWineType}
        />
        <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handlePDF(e.target.files[0]); e.target.value = ""; }} />

        {history.length > 0 && (
          <div className="flex justify-end">
            <button onClick={() => setShowHist(true)}
              className="px-3 py-1.5 rounded-[7px] text-[12px] cursor-pointer font-sans bg-transparent border border-border text-secondary">
              {history.length} fiches
            </button>
          </div>
        )}


        {/* Result */}
        <div id="result-panel" role="tabpanel" aria-live="polite">
          {result && (
            <ChatResultPanel
              key={key}
              copied={copied}
              lang={lang}
              modLabel={mod?.label}
              modNumber={mod?.n}
              result={result}
              tab={tab}
              wine={wine}
              onCopy={() => { navigator.clipboard.writeText(result); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              onExportPDF={exportPDF}
              onShowQR={() => setShowQR(true)}
              onUpdateResult={updateResult}
            />
          )}
        </div>
      </div>
    </main>
  );
}
